import * as React from 'react';
import { Checkbox, FormControlLabel } from '@mui/material'
import { TreeItem, SimpleTreeView } from '@mui/x-tree-view';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useEffect } from 'react';
import { SxProps } from '@mui/material/styles';

/*
*   Древовидный список чекбоксов.
*
*   Входные данные:
*       items                     - [обязательный]    - сложный объект, который реализует интерфейс ITreeViewCheckboxItem.
*       selectedItems             - [не обязательный] - массив из уже выбранных элементов, помогает сохранить состояние, если элемент скрылся с экрана (например, нужно показать другой компонент в из сосдней влкдки и вернуться обратно).
*       handleSetSelectedItems    - [обязательный]    - функция, передает массив с выбранными значениями в родительский компонент.
*       formControlLabelStyle     - [не обязательный] - css-стилизация компонента FormControlLabel (строка в списке).
*
*   Выходные данные:
*       string[] - массив из строк, где каждое значение - это id (ключ) выбранного элемента.
*/

type TreeViewCheckboxListProps = {
    items: ITreeViewCheckboxItem,
    selectedItems?: string[],
    handleSetSelectedItems: (items: string[]) => void,
    treeViewStyle?: SxProps,
    formControlLabelStyle?: SxProps,
    isChecked?: boolean,
    defaultExpandedItems?: string[],
}

export type ITreeViewCheckboxItem = {
    id: string,
    name: string,
    children?: ITreeViewCheckboxItem[],
}


/* 
*   Вспомогательные функции 
*/
// Проходит рекурсией по всем узлам дерева
const goThroughAllNodes = (nodes: ITreeViewCheckboxItem, map: Record<string, any> = {}) => {
    if (!nodes.children)
        return null;

    map[nodes.id] = getAllChild(nodes).splice(1);

    for (const childNode of nodes.children)
        goThroughAllNodes(childNode, map);

    return map;
}

// Получает рекурсией все элементы у выбранного узла
const getAllChild = (childNode: ITreeViewCheckboxItem | null, collectedNodes: any[] = []) => {
    if (childNode === null)
        return collectedNodes;

    collectedNodes.push(childNode.id);

    if (Array.isArray(childNode.children)) {
        for (const node of childNode.children) {
            getAllChild(node, collectedNodes);
        }
    }

    return collectedNodes;
}

// Получает элемент дерева по его ID
const getChildById = (nodes: ITreeViewCheckboxItem, id: string) => {
    const array: string[] = [];
    const path: string[] = [];

    const getNodeById = (node: ITreeViewCheckboxItem, id: string, parentsPath: string[]): any => {
        let result = null;

        if (node.id === id) {
            return node;
        }
        else if (Array.isArray(node.children)) {
            for (const childNode of node.children) {
                result = getNodeById(childNode, id, parentsPath);

                if (result) {
                    parentsPath.push(node.id);
                    return result;
                }
            }
            return result;
        }
        return result;
    }
    const nodeToToggle = getNodeById(nodes, id, path);

    return { childNodesToToggle: getAllChild(nodeToToggle, array), path };
};


/*  
*   Главная функция компонента 
*/
export function TreeViewCheckboxList({
    items,
    selectedItems = [],
    handleSetSelectedItems,
    formControlLabelStyle,
    treeViewStyle,
    isChecked = true,
    defaultExpandedItems = ['all'],
}: TreeViewCheckboxListProps) {
    const [selected, setSelected] = React.useState<string[]>((selectedItems.length > 0 ? selectedItems : isChecked ? getAllChild(items).splice(1) : []) || []);

    // Передает текущий список выбранных элементов в родительский компонент
    useEffect(() => {
        handleSetSelectedItems(selected)
    }, [selected]);

    const parentMap = React.useMemo(() => goThroughAllNodes(items), []);
    const selectedSet = React.useMemo(() => new Set(selected), [selected]);

    const handleChangeCheckboxState = (checked: boolean, nodes: ITreeViewCheckboxItem) => {
        const { childNodesToToggle, path } = getChildById(items, nodes.id);
        let array: string[] = [];
        if (checked) {
            const isAll = parentMap?.['all']?.every((childNodeId: string) => selectedSet.has(childNodeId) || path.includes(childNodeId) || childNodesToToggle.includes(childNodeId))
                && path?.every((cNodeId: string) => cNodeId === 'all' || parentMap?.[cNodeId]?.every((childNodeId: string) => selectedSet.has(childNodeId) || childNodesToToggle.includes(childNodeId)));

            array = [
                ...selected,
                ...childNodesToToggle,
                ...(isAll ? ['all'] : []),
            ];
        }
        else {
            array = selected
                .filter((value) => !childNodesToToggle.includes(value))
                .filter((value) => !path.includes(value));
        }

        array = array.filter((v, i) => array.indexOf(v) === i);
        setSelected(array);
    }

    // Отрисовка всех узлов дерева
    const renderTree = (nodes: ITreeViewCheckboxItem) => {
        if (parentMap === null)
            return;

        const allSelectedChildren = parentMap[nodes.id]?.every((childNodeId: string) => selectedSet.has(childNodeId));
        const checked = selectedSet.has(nodes.id) || allSelectedChildren || false;
        const indeterminate = parentMap[nodes.id]?.some((childNodeId: string) => selectedSet.has(childNodeId)) || false;

        return (
            <TreeItem
                key={nodes.id}
                itemId={nodes.id}
                label={
                    <FormControlLabel
                        sx={formControlLabelStyle}
                        control={
                            <Checkbox
                                checked={checked}
                                indeterminate={!checked && indeterminate}
                                onChange={(event: any) => handleChangeCheckboxState(event.currentTarget.checked, nodes)}
                                onClick={(e) => e.stopPropagation()}
                            />
                        }
                        label={<>{nodes.name}</>}
                        key={nodes.id}
                    />
                }
            >
                {Array.isArray(nodes.children)
                    ? nodes.children.map((node) => renderTree(node))
                    : null}
            </TreeItem>
        );
    };

    return (
        /* Вызов рендера дерева */
        <SimpleTreeView
            defaultExpandedItems={defaultExpandedItems}
            sx={{
                ...treeViewStyle,
                '.MuiTreeItem-content': {
                    padding: '0px 8px',
                },
            }}
            slots={{
                expandIcon: () => <ChevronRightIcon />,
                collapseIcon: () => <ExpandMoreIcon />,
            }}
        >
            {renderTree(items)}
        </SimpleTreeView>
    );
}